import { Crop, RotateCcw, ZoomIn } from 'lucide-react'
import * as React from 'react'
import type { Area, Point } from 'react-easy-crop'
import Cropper from 'react-easy-crop'

import { getCroppedImage } from '@/lib/crop-utils'
import { toast } from '@/hooks/use-toast'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import uploadService from '@/services/upload.service'

// ============================================
// TYPES
// ============================================

interface ImageEditorDialogProps {
  /** Whether the dialog is open */
  open: boolean
  /** Callback when the dialog is closed */
  onOpenChange: (open: boolean) => void
  /** The image URL to edit */
  imageSrc: string
  /** Callback when the cropped image is ready (new URL after upload) */
  onCropComplete: (newImageUrl: string) => void
}

interface AspectRatioOption {
  label: string
  value: number | undefined
}

const ASPECT_RATIOS: AspectRatioOption[] = [
  { label: 'Free', value: undefined },
  { label: '16:9', value: 16 / 9 },
  { label: '4:3', value: 4 / 3 },
  { label: '1:1', value: 1 },
  { label: '3:4', value: 3 / 4 },
  { label: '9:16', value: 9 / 16 },
]

// ============================================
// IMAGE EDITOR DIALOG
// ============================================

/**
 * Image editor dialog with visual crop, zoom, and aspect ratio controls.
 * Uses react-easy-crop for the interactive crop area.
 * On "Apply Crop", crops the image on a canvas and re-uploads via uploadService.
 */
export function ImageEditorDialog({ open, onOpenChange, imageSrc, onCropComplete }: ImageEditorDialogProps) {
  const [crop, setCrop] = React.useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = React.useState(1)
  const [rotation, setRotation] = React.useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(null)
  const [selectedAspect, setSelectedAspect] = React.useState<number | undefined>(undefined)
  const [isApplying, setIsApplying] = React.useState(false)

  // Reset state when dialog opens with a new image
  React.useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setRotation(0)
      setSelectedAspect(undefined)
      setCroppedAreaPixels(null)
    }
  }, [open, imageSrc])

  const handleCropComplete = React.useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels)
  }, [])

  const handleReset = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    setSelectedAspect(undefined)
  }

  const handleApplyCrop = async () => {
    if (!croppedAreaPixels) return

    setIsApplying(true)
    try {
      // Crop the image on canvas
      const croppedBlob = await getCroppedImage(imageSrc, croppedAreaPixels)

      // Create a File from the Blob for upload
      const croppedFile = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' })

      // Upload the cropped image
      const result = await uploadService.uploadImage({ image: croppedFile })

      onCropComplete(result.url)
      onOpenChange(false)
      toast({ title: 'Image cropped', description: 'Cropped image uploaded successfully' })
    } catch {
      toast({ title: 'Crop failed', description: 'Failed to crop and upload image', variant: 'destructive' })
    } finally {
      setIsApplying(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <DialogHeader>
          <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Crop style={{ width: '20px', height: '20px' }} />
            Edit Image
          </DialogTitle>
          <DialogDescription>
            Drag to reposition, scroll to zoom, then click Apply to crop and upload.
          </DialogDescription>
        </DialogHeader>

        {/* Crop Area */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '400px',
            backgroundColor: '#1a1a1a',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={selectedAspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={handleCropComplete}
            showGrid
            style={{
              containerStyle: { borderRadius: '8px' },
            }}
          />
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Aspect Ratio */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', minWidth: '80px' }}>Aspect Ratio:</span>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {ASPECT_RATIOS.map(ratio => (
                <button
                  key={ratio.label}
                  type="button"
                  onClick={() => setSelectedAspect(ratio.value)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: selectedAspect === ratio.value ? '#3b82f6' : '#d1d5db',
                    backgroundColor: selectedAspect === ratio.value ? '#eff6ff' : '#fff',
                    color: selectedAspect === ratio.value ? '#2563eb' : '#374151',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {ratio.label}
                </button>
              ))}
            </div>
          </div>

          {/* Zoom */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ZoomIn style={{ width: '16px', height: '16px', color: '#6b7280', flexShrink: 0 }} />
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', minWidth: '48px' }}>Zoom:</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
              style={{ flex: 1, accentColor: '#3b82f6' }}
            />
            <span style={{ fontSize: '12px', color: '#9ca3af', minWidth: '36px', textAlign: 'right' }}>
              {zoom.toFixed(1)}x
            </span>
          </div>

          {/* Rotation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RotateCcw style={{ width: '16px', height: '16px', color: '#6b7280', flexShrink: 0 }} />
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', minWidth: '48px' }}>Rotate:</span>
            <input
              type="range"
              min={0}
              max={360}
              step={1}
              value={rotation}
              onChange={e => setRotation(Number(e.target.value))}
              style={{ flex: 1, accentColor: '#3b82f6' }}
            />
            <span style={{ fontSize: '12px', color: '#9ca3af', minWidth: '36px', textAlign: 'right' }}>
              {rotation}°
            </span>
          </div>
        </div>

        <DialogFooter style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              color: '#374151',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Reset
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                color: '#374151',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyCrop}
              disabled={isApplying || !croppedAreaPixels}
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: isApplying ? '#93c5fd' : '#3b82f6',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: isApplying ? 'not-allowed' : 'pointer',
                opacity: isApplying ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {isApplying ? 'Applying...' : 'Apply Crop'}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
